/**
 * Funnel beacon — best-effort POST of the two browser-only session facts to
 * `analytics.beacon` on apps/api.
 *
 * The server owns every activation milestone (signup, first recipe, first
 * version, first cook, W1 return); it emits them from the authoritative
 * mutation point. Only two facts cannot be derived server-side, and this
 * module carries exactly those:
 *
 *   - `pwa_installed`  — the window `appinstalled` event.
 *   - `session_start`  — carries the CSS `display-mode`, giving the
 *                        standalone-vs-browser session split (the quantitative
 *                        evidence file the Capacitor wishlist trigger waits on).
 *
 * Strict invariants (mirrors `errors/report.ts`, deliberately):
 *
 *   1. **Best-effort.** Never throws, never blocks, never retries. A dropped
 *      beacon is a lost datapoint, not a user-visible failure.
 *   2. **Privacy.** The payload is two closed enums. There is no field a
 *      recipe title, body, note, handle, or email could travel in — by
 *      construction, not by convention.
 *   3. **Server-stamped identity.** The endpoint reads userUuid from the
 *      session; the client never declares who it is.
 *   4. **No milestone claims.** The client cannot assert that it created a
 *      recipe or logged a cook. It reports only what the browser knows.
 *   5. **No new dependencies.** Plain `globalThis.fetch`, same
 *      `credentials: "include"` + `X-CSRF-Token` shape as the rest of the API.
 *
 * Plan ref: .plan/features/2026-07-24-r2-funnel-activation-instrumentation.md §3
 */

import { getCsrfHeader } from "../auth/csrf.js";

// ── Types ─────────────────────────────────────────────────────────────────────

/** Closed set matching the server's `DisplayModeSchema`. */
export type DisplayMode = "standalone" | "browser" | "minimal-ui" | "fullscreen" | "unknown";

export type BeaconEvent = "session_start" | "pwa_installed";

export interface FunnelBeaconOptions {
  /** API origin override; defaults to "" (same-origin via the router Worker). */
  baseUrl?: string;
  /** Override fetch — used by tests. */
  fetch?: typeof fetch;
}

// ── Display-mode detection ────────────────────────────────────────────────────

/**
 * Resolve the current display mode from the CSS `display-mode` media feature.
 *
 * Checked most-installed-first so an installed app never reports "browser".
 * iOS Safari's legacy `navigator.standalone` is consulted as a fallback because
 * older iOS builds do not match `(display-mode: standalone)` — and iOS is the
 * platform whose install rate the Capacitor decision actually turns on.
 */
export function detectDisplayMode(): DisplayMode {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "unknown";
  }
  const modes: DisplayMode[] = ["fullscreen", "standalone", "minimal-ui", "browser"];
  for (const mode of modes) {
    try {
      if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
    } catch {
      // A browser that rejects the query cannot answer for any mode — stop
      // asking rather than looping through four identical failures.
      break;
    }
  }
  // iOS Safari legacy standalone flag (non-standard, still the only signal on
  // older iOS versions).
  const legacy = (navigator as unknown as { standalone?: boolean }).standalone;
  if (legacy === true) return "standalone";
  return "unknown";
}

// ── Send ──────────────────────────────────────────────────────────────────────

/**
 * POST one beacon. Resolves to `true` on a 2xx, `false` on anything else.
 * The boolean exists for tests; production callers ignore it.
 */
export async function sendFunnelBeacon(
  event: BeaconEvent,
  displayMode: DisplayMode,
  options: FunnelBeaconOptions = {},
): Promise<boolean> {
  const f = options.fetch ?? globalThis.fetch;
  if (typeof f !== "function") return false;

  const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${baseUrl}/rpc/analytics/beacon`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = getCsrfHeader();
  if (csrf) headers["X-CSRF-Token"] = csrf;

  const init: RequestInit = {
    method: "POST",
    credentials: "include",
    headers,
    // oRPC's HTTP transport takes `{ json: <input>, meta: [] }`.
    body: JSON.stringify({ json: { event, displayMode }, meta: [] }),
  };
  // Never let telemetry hold page teardown open.
  if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
    init.signal = AbortSignal.timeout(5_000);
  }

  try {
    const res = await f(url, init);
    return res.ok;
  } catch {
    // Swallowed deliberately — invariant 1. There is no user action behind a
    // beacon, so there is nothing to surface (this is not a Rule 8 surface).
    return false;
  }
}

// ── Session-scoped guard ──────────────────────────────────────────────────────

/** sessionStorage key guarding the once-per-browser-session `session_start`. */
export const SESSION_BEACON_KEY = "proof.funnel.session-beacon";

/**
 * Has the session-start beacon already fired in this browser session?
 *
 * `sessionStorage` is the right scope: it resets on a new tab / new launch,
 * which is exactly the boundary "a session" means for the standalone-vs-browser
 * split. Any storage error (Safari private mode, disabled storage) is treated
 * as "not yet fired" so the beacon still sends once per page load — an
 * over-count on a locked-down browser is preferable to a silent zero.
 */
export function hasSessionBeaconFired(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(SESSION_BEACON_KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark the session-start beacon as fired. Storage failures are ignored. */
export function markSessionBeaconFired(): void {
  try {
    globalThis.sessionStorage?.setItem(SESSION_BEACON_KEY, "1");
  } catch {
    // Non-fatal: worst case the beacon fires again on the next page load.
  }
}

/**
 * Fire the session-start beacon at most once per browser session.
 *
 * Marks the guard BEFORE awaiting, so two effects racing on the same tick
 * (React StrictMode double-invoke) send one beacon, not two.
 */
export async function sendSessionStartOnce(options: FunnelBeaconOptions = {}): Promise<boolean> {
  if (hasSessionBeaconFired()) return false;
  markSessionBeaconFired();
  return sendFunnelBeacon("session_start", detectDisplayMode(), options);
}
